import { AlertDialog } from 'radix-ui';
import { createContext, useCallback, useContext, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from './button';
import { useReturnFocus } from './focus';

/**
 * One way to ask "are you sure" (port of apps/web/src/utils/confirm.ts).
 *
 *   const { confirmDestructive, confirmWithReason } = useConfirm();
 *   if (!(await confirmDestructive({ title: 'Delete user', body: '…', confirmLabel: 'Delete' }))) return;
 *   const reason = await confirmWithReason({ title: 'Suspend user', body: '…' }); // string | null
 *
 * Declining is a normal outcome and never throws. The body names the
 * CONSEQUENCE ("They lose access immediately"), not the action.
 */
export type ConfirmOptions = {
  /** Name the operation: "Delete user", "Stop trial". */
  title: string;
  /** What happens if they continue. */
  body: ReactNode;
  /** A verb matching the title. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling on the confirm button. Default true. */
  danger?: boolean;
};

export type ConfirmWithReasonOptions = ConfirmOptions & {
  reasonPlaceholder?: string;
  /** Minimum trimmed length. Default 3 — an audit entry reading "ok" is not one. */
  minimumReasonLength?: number;
};

type Request =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'reason'; options: ConfirmWithReasonOptions; resolve: (value: string | null) => void };

type ConfirmApi = {
  confirmDestructive: (options: ConfirmOptions) => Promise<boolean>;
  confirmWithReason: (options: ConfirmWithReasonOptions) => Promise<string | null>;
};

const ConfirmContext = createContext<ConfirmApi | null>(null);

export function useConfirm(): ConfirmApi {
  const api = useContext(ConfirmContext);
  if (!api) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return api;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const settled = useRef(false);
  const reasonId = useId();
  const returnFocus = useReturnFocus();
  const errorId = useId();

  const confirmDestructive = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        settled.current = false;
        setRequest({ kind: 'confirm', options, resolve });
      }),
    [],
  );
  const confirmWithReason = useCallback(
    (options: ConfirmWithReasonOptions) =>
      new Promise<string | null>((resolve) => {
        settled.current = false;
        setReason('');
        setTouched(false);
        setRequest({ kind: 'reason', options, resolve });
      }),
    [],
  );
  const api = useMemo(() => ({ confirmDestructive, confirmWithReason }), [confirmDestructive, confirmWithReason]);

  const settle = (accepted: boolean) => {
    if (!request || settled.current) return;
    settled.current = true;
    if (request.kind === 'confirm') request.resolve(accepted);
    else request.resolve(accepted ? reason.trim() : null);
    setRequest(null);
  };

  const options = request?.options;
  const minimum = request?.kind === 'reason' ? (request.options.minimumReasonLength ?? 3) : 0;
  const reasonValid = reason.trim().length >= minimum;
  const danger = options?.danger ?? true;

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      <AlertDialog.Root open={request !== null} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=open]:animate-fade-in" />
          <AlertDialog.Content
            onOpenAutoFocus={returnFocus.onOpenAutoFocus}
            onCloseAutoFocus={returnFocus.onCloseAutoFocus}
            className="fixed top-1/2 left-1/2 z-50 flex w-[calc(100vw-32px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-card border border-line bg-surface p-6 text-fg shadow-pop outline-none data-[state=open]:animate-pop-in">
            <AlertDialog.Title className="m-0 text-base font-semibold">{options?.title}</AlertDialog.Title>
            <AlertDialog.Description asChild>
              <div className="m-0 text-sm text-muted">{options?.body}</div>
            </AlertDialog.Description>
            {request?.kind === 'reason' ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={reasonId} className="text-xs font-medium text-muted">Reason</label>
                <textarea
                  id={reasonId}
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); }}
                  onBlur={() => { setTouched(true); }}
                  placeholder={request.options.reasonPlaceholder ?? 'Reason for this change'}
                  rows={3}
                  aria-invalid={touched && !reasonValid}
                  aria-describedby={touched && !reasonValid ? errorId : undefined}
                  className="resize-y rounded-ctl border border-line bg-surface px-2.5 py-2 text-sm placeholder:text-faint"
                />
                {touched && !reasonValid ? (
                  <span id={errorId} className="text-xs text-neg-ink">Enter at least {minimum} characters</span>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <Button>{options?.cancelLabel ?? 'Cancel'}</Button>
              </AlertDialog.Cancel>
              <Button
                variant={danger ? 'danger' : 'primary'}
                disabled={!reasonValid}
                onClick={() => { settle(true); }}
              >
                {options?.confirmLabel ?? (request?.kind === 'reason' ? 'Continue' : 'Confirm')}
              </Button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
}
